import { ActionTree } from 'vuex';
import { DealboardHelper } from '@/dealboard/helpers/dealboard.helper';
import {
  mutationSetDeals,
  mutationAddToBank,
  mutationSetBank,
  mutationSetLoading,
  mutationToggleReminder,
  mutationSetPagination,
} from '@/dealboard/store/dealboard.mutations';
import { IDealboardState, state as defaultState } from '@/dealboard/store/dealboard.store';
import { IDeal } from '@/dealboard/types/deal';
import { RootState } from '@/store/store';
import { DealboardService } from '@/api/dealboard/services/dealboard.service';
import { IDealDocument } from '@/dealboard/types/deal-document';

export const actionLoadDealboardTotalValue = 'loadDealboardTotalValue';

export const actionLoadDealboard = 'loadDealboard';
export const actionLoadDealboardSuccess = 'loadDealboardSuccess';
export const actionLoadDealboardFailed = 'loadDealboardFailed';

export const actionInsertInitialDeals = 'insertInitialDeals';
export const actionIncreaseDeals = 'increaseDeals';

export const actionLoadDealScore = 'loadDealScore';
export const actionLoadDealScoreSuccess = 'loadDealScoreSuccess';
export const actionLoadDealScoreFailed = 'loadDealScoreFailed';

export const actionToggleReminder = 'toggleReminder';
export const actionToggleReminderSuccess = 'toggleReminderSuccess';

export const actions: ActionTree<IDealboardState, RootState> = {
  async [actionLoadDealboardTotalValue](
    { commit },
    payload: {
      filter: string;
      showTeam: boolean;
      showAll: boolean;
      scope: string[];
    }
  ) {
    try {
      const response = await DealboardService.loadDeals(
        payload.filter,
        payload.showTeam,
        payload.showAll,
        payload.scope
      );
      commit(mutationSetBank, { totalValue: response.totalValue });
    } catch (error) {
      commit(mutationSetBank, { totalValue: 0 });
    }
  },
  async [actionLoadDealboard](
    { commit, state, dispatch },
    payload: {
      filter: string;
      showTeam: boolean;
      showAll: boolean;
      scope: string[];
      sortBy?: string;
      sortOrder?: string;
      next?: boolean;
      usePagination?: boolean;
      fetchDealscore?: boolean;
    }
  ) {
    commit(mutationSetLoading, true);

    if (!payload.next) {
      commit(mutationSetPagination, defaultState.pagination);
      commit(mutationSetBank, { deals: [], totalValue: 0 });
      commit(mutationSetDeals, []);
    }

    let offset: number, limit: number;
    if (payload.usePagination) {
      limit = state.pagination.pageSize;
      const currentPage =
        payload.next === true ? state.pagination.currentPage + 1 : state.pagination.currentPage;
      offset = currentPage * state.pagination.pageSize;
    }

    try {
      const { deals, totalValue } = await DealboardService.loadDeals(
        payload.filter,
        payload.showTeam,
        payload.showAll,
        payload.scope,
        payload.sortBy,
        payload.sortOrder,
        limit,
        offset
      );
      dispatch(actionLoadDealboardSuccess, {
        deals,
        totalValue,
        next: payload.next,
      });
      if (payload.fetchDealscore) {
        dispatch(actionLoadDealScore, deals);
      }
    } catch (error) {
      dispatch(actionLoadDealboardFailed, error);
    }
  },
  [actionLoadDealboardSuccess](
    { commit, state, dispatch },
    payload: { deals: IDeal[]; totalValue: number; next: boolean }
  ) {
    if (payload.next) {
      commit(mutationAddToBank, { deals: payload.deals });
      commit(mutationSetPagination, { ...state.pagination, totalRows: payload.deals.length });
      dispatch(actionIncreaseDeals);
    } else {
      commit(mutationSetBank, payload);
      commit(mutationSetPagination, { ...state.pagination, totalRows: payload.deals.length });
      dispatch(actionInsertInitialDeals, payload.deals);
    }
    commit(mutationSetLoading, false);
  },
  [actionLoadDealboardFailed]({ commit }, error: any) {
    commit(mutationSetLoading, false);
    console.error(error);
  },
  async [actionLoadDealScore]({ dispatch }, deals: IDeal[]) {
    try {
      const documentIds: string[] = deals.map((deal: IDeal) => deal.document.id);
      const documents: IDealDocument[] = await DealboardService.loadDealScore(documentIds);
      const updatedDeals: IDeal[] = deals.map((deal: IDeal) =>
        documents.some((document: IDealDocument) => document.id === deal.document.id)
          ? {
              ...deal,
              document: {
                ...deal.document,
                latestDealScore: documents.find(
                  (document: IDealDocument) => document.id === deal.document.id
                ).latestDealScore,
              },
            }
          : deal
      );
      dispatch(actionLoadDealScoreSuccess, updatedDeals);
    } catch (error) {
      dispatch(actionLoadDealScoreFailed, error);
    }
  },
  [actionLoadDealScoreSuccess]({ commit }, deals: IDeal[]) {
    commit(mutationSetDeals, deals);
  },
  [actionLoadDealScoreFailed](_, error: any) {
    console.error('Could not load deal scores', error);
  },
  [actionInsertInitialDeals]({ commit, state }, deals: IDeal[]) {
    const paginatedDeals: IDeal[] = DealboardHelper.getPaginatedDeals(deals, state.pagination);
    commit(mutationSetDeals, paginatedDeals);
  },
  [actionIncreaseDeals]({ commit, state }) {
    commit(mutationSetPagination, {
      ...state.pagination,
      currentPage: ++state.pagination.currentPage,
    });
    const paginatedDeals: IDeal[] = DealboardHelper.getPaginatedDeals(
      state.dealBank,
      state.pagination
    );
    commit(mutationSetDeals, [...state.deals, ...paginatedDeals]);
  },
  async [actionToggleReminder](
    { dispatch },
    payload: {
      documentId: string;
      value: boolean;
    }
  ) {
    if (!payload.documentId) {
      return;
    }
    const status: number = await DealboardService.toggleReminders(payload);
    if (status === 1) {
      dispatch(actionToggleReminderSuccess, {
        documentId: payload.documentId,
        value: payload.value,
      });
    }
  },
  async [actionToggleReminderSuccess](
    { commit },
    payload: {
      documentId: string;
      value: boolean;
    }
  ) {
    commit(mutationToggleReminder, {
      documentId: payload.documentId,
      reminderValue: payload.value,
    });
  },
};
